import { Button } from '@/components/ui/button';
import { Calendar as UiCalendar } from '@/components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { addDays, format } from 'date-fns';
import { Calendar, CalendarIcon, Target } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';



interface Story {
  id: string;
  name: string;
}

interface GoalFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (goal: {
    name: string;
    deadline: string;
    storyId?: number;
    projectId?: number;
  }) => Promise<void>;
  stories: Story[];
  loading?: boolean;
}

export default function GoalForm({ open, onOpenChange, onSubmit, stories, loading = false }: GoalFormProps) {
  const { projectId: projectIdParam } = useParams();
  const projectId = projectIdParam ? parseInt(projectIdParam) : undefined;
  const [goalName, setGoalName] = useState<string>('');
  const [selectedStory, setSelectedStory] = useState<string>('');
  const [date, setDate] = useState<Date>(addDays(new Date(), 30));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localStories, setLocalStories] = useState<Story[]>(stories || []);
  const [isLoadingStories, setIsLoadingStories] = useState(false);

  // Fetch stories if none are provided
  useEffect(() => {
    const fetchStories = async () => {
      if (stories && stories.length > 0) {
        setLocalStories(stories);
        return;
      }

      if (!projectId) return;

      try {
        setIsLoadingStories(true);
        const { supabase } = await import('@/services/supabase/supabase');

        const { data, error } = await supabase
          .from('stories')
          .select('id, name')
          .eq('project_id', projectId);

        if (error) throw error;

        setLocalStories(data || []);
      } catch (error) {
        console.error('Error fetching stories:', error);
      } finally {
        setIsLoadingStories(false);
      }
    };

    fetchStories();
  }, [projectId, stories]);

  // Simplify the handleSubmit function
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!goalName.trim()) {
      toast({
        title: "Error",
        description: "Please enter a goal name",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit({
        name: goalName,
        storyId: selectedStory === "all_stories" ? undefined : selectedStory ? parseInt(selectedStory) : undefined,
        deadline: format(date, 'yyyy-MM-dd'),
        projectId: projectId,
      });

      // Reset form
      setGoalName('');
      setSelectedStory('');
      setDate(addDays(new Date(), 30));

      // Close dialog
      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting goal:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update the dialog title and content
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] sm:rounded-2xl border-border/60 shadow-2xl p-0 overflow-hidden gap-0">
        <DialogHeader className="p-8 pb-6 border-b border-border/40 bg-muted/5 space-y-1">
          <DialogTitle className="flex items-center text-2xl font-semibold text-foreground tracking-tight">
            <Target className="mr-2 h-5 w-5 text-primary" />
            Create New Goal
          </DialogTitle>
          <DialogDescription className="text-base text-muted-foreground leading-relaxed">
            Set achievable goals to track your progress
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col">
          <div className="space-y-6 px-8 py-8">
            <div className="grid gap-2.5">
              <Label htmlFor="goalName" className="flex gap-1 items-center text-sm font-medium text-foreground/80 pl-1">
                Goal Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="goalName"
                type="text"
                value={goalName}
                onChange={(e) => setGoalName(e.target.value)}
                placeholder="Enter a name for your goal"
                className="w-full h-11 text-base bg-muted/30 border-muted-foreground/10 hover:border-muted-foreground/20 focus:border-ring focus:bg-background transition-all rounded-xl"
              />
            </div>

            <div className="grid gap-2.5">
              <Label htmlFor="story" className="flex gap-1 items-center text-sm font-medium text-foreground/80 pl-1">
                Story (Optional)
              </Label>
              <Select
                value={selectedStory}
                onValueChange={setSelectedStory}
                disabled={isLoadingStories || localStories.length === 0}
              >
                <SelectTrigger className="w-full h-11 text-base bg-muted/30 border-muted-foreground/10 hover:border-muted-foreground/20 focus:border-ring focus:bg-background transition-all rounded-xl">
                  <SelectValue placeholder={isLoadingStories ? "Loading stories..." : "Select a story"} />
                </SelectTrigger>
                <SelectContent className="rounded-xl border-border/60 shadow-xl">
                  <SelectItem value="all_stories" className="rounded-lg">All stories</SelectItem>
                  {localStories.map((story) => (
                    <SelectItem key={story.id} value={story.id} className="rounded-lg">
                      {story.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2.5">
              <Label className="flex gap-1 items-center text-sm font-medium text-foreground/80 pl-1">
                <Calendar className="mr-1 h-3.5 w-3.5" />
                Deadline <span className="text-destructive">*</span>
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full h-11 justify-start text-left font-normal text-base bg-muted/30 border-muted-foreground/10 hover:border-muted-foreground/20 hover:bg-muted/50 transition-all rounded-xl"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                    {date ? format(date, 'PPP') : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 rounded-xl shadow-xl border-border/60" align="start">
                  <UiCalendar
                    mode="single"
                    selected={date}
                    onSelect={(newDate) => newDate && setDate(newDate)}
                    initialFocus
                    disabled={(date) => date < new Date()}
                    className="rounded-xl border-none"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <DialogFooter className="p-6 bg-muted/5 border-t border-border/40 flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="min-w-[100px] rounded-xl hover:bg-muted/50"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || loading || !goalName.trim()}
              className="min-w-[120px] rounded-xl font-medium shadow-sm active:scale-95 transition-all text-base bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {isSubmitting || loading ? 'Creating...' : 'Create Goal'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

}